import React from 'react';

import clone from 'lodash/clone';

import {StyledEditableStepFieldOrChoiceBoxDiv} from 'app/procedures/styles';

import {StepEditCheckboxField} from './checkbox-field';
import {StepEditDateField} from './date-field';
import {StepEditFileField} from './file-field';
import {StepEditRadiobuttonField} from './radiobutton-field';
import {ReorderOrDeleteButton} from './ReorderOrDeleteButton';
import {StepEditTextField} from './text-field';
import {StepEditTextareaField} from './textarea-field';

import type {ChoicesStepFieldType, StepFieldType} from 'sweet-types';

interface Props<T extends StepFieldType> {
  field: T;
  fieldIndex: number;
  numFields: number;
  onRemoveField: (removeField: T) => void;
  onChangeField: (changedField: T) => void;
  onReorderField: (index: number, newIndex: number) => void;
}

export function StepEditFieldWrapper<T extends StepFieldType>({
  field,
  fieldIndex,
  numFields,
  onRemoveField,
  onChangeField,
  onReorderField,
}: Props<T>): JSX.Element {
  function changeLabel(label: string) {
    const newField: T = clone(field);
    newField.label = label;
    onChangeField(newField);
  }

  function changeChoices(
    choices: Array<{
      label: string;
      id: string;
    }>
  ) {
    const newField: T = clone(field);

    if ('choices' in field) {
      (newField as ChoicesStepFieldType).choices = choices;
    }

    onChangeField(newField);
  }

  const {type, label} = field;
  const InputField = fieldsMap[type] || StepEditTextField;

  return (
    <StyledEditableStepFieldOrChoiceBoxDiv
      className={`my-2 ${type} flex-grow-1 vertical-input-group`}
    >
      <InputField
        optionsButton={
          <ReorderOrDeleteButton
            itemIndex={fieldIndex}
            numItems={numFields}
            onReorder={onReorderField}
            onRemove={() => onRemoveField(field)}
          />
        }
        onChange={changeLabel}
        label={label}
        onChangeChoices={changeChoices}
        choices={'choices' in field ? field.choices : undefined}
      />
    </StyledEditableStepFieldOrChoiceBoxDiv>
  );
}

const fieldsMap = {
  text: StepEditTextField,
  textarea: StepEditTextareaField,
  checkbox: StepEditCheckboxField,
  date: StepEditDateField,
  radio: StepEditRadiobuttonField,
  file: StepEditFileField,
};
